import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  calendarWeeksAgo,
  countsAsStrength,
  DEFAULT_WEEK_START_DAY,
  inTimeRange,
  weekWindowStart,
  type ReportSectionId,
  type TimeRange,
} from "@heraion/shared";

import { WidgetColumns } from "../Home/WidgetColumns";
import { E1RMTrendsPanel } from "./E1RMTrendsPanel";
import { MovementBalancePanel } from "./MovementBalancePanel";
import { computeWeeklyCounts } from "./progressMath";
import { ProgressPrTimelineCard, type MaxHistory } from "./ProgressPrTimelineCard";
import { PeriodOnScreenContext, ProgressPictureContext } from "./InfoPill";
import { ProgressRhythmCard } from "./ProgressRhythmCard";
import { PROGRESS_WIDGET_COLUMN } from "./progressWidgetCatalog";
import {
  ReportPageHeader,
  ReportPeriodText,
  type ReportPageNumber,
} from "./ReportPageHeader";
import type { RowSlice } from "./reportSectionRows";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";

type FormatDate = (iso: string, locale: string) => string;

// PG-64: Rhythm over the report's period, from the period's session results
// (the host's session list covers only Progress's 12 weeks).
function ReportRhythm({
  deps,
  formatDate,
  locale,
}: {
  deps: ProgressDeps & { range: TimeRange };
  formatDate: FormatDate;
  locale: string;
}) {
  const { range, theme } = deps;
  const weekStartDay = deps.weekStartDay ?? DEFAULT_WEEK_START_DAY;
  const { results, error } = useSessionResultsFromDeps(deps);
  const data = useMemo(() => {
    if (!results) return null;
    const inPeriod = results
      .filter((r) => inTimeRange(r.startedAtMs, range) && countsAsStrength(r.result))
      .map((r) => ({ startedAt: new Date(r.startedAtMs).toISOString() }));
    const weeks = calendarWeeksAgo(range.fromMs, range.toMs, weekStartDay) + 1;
    const last = inPeriod.reduce<string | null>(
      (acc, s) => (acc === null || s.startedAt > acc ? s.startedAt : acc),
      null,
    );
    return {
      summary: {
        total: inPeriod.length,
        last7d: 0,
        last30d: 0,
        lastSession: last === null ? null : { startedAt: last },
      },
      counts: computeWeeklyCounts(inPeriod, range.toMs, weeks, weekStartDay),
      // The period's last week is still running only if it is this week.
      partialLast:
        weekWindowStart(range.toMs, weekStartDay) ===
        weekWindowStart(Date.now(), weekStartDay),
    };
  }, [results, range, weekStartDay]);
  return (
    <ProgressRhythmCard
      theme={theme}
      summary={data?.summary ?? null}
      summaryError={error}
      rhythmCounts={data?.counts ?? []}
      formatDate={formatDate}
      locale={locale}
      scope="period"
      partialLast={data?.partialLast ?? false}
    />
  );
}

// PG-64: the report itself, the chosen sections over one period. The SAME
// panels Progress mounts, handed the period instead of a trailing window.
// Drawn on screen, and branded for each page of the shared images.
export function ProgressReportBody({
  deps,
  order,
  wide,
  formatDate,
  locale,
  maxHistory,
  branded = false,
  page,
  slice,
}: {
  /** Carries the period: `range` and `resultsRange` are both set. */
  deps: ProgressDeps & { range: TimeRange };
  order: readonly ReportSectionId[];
  wide: boolean;
  formatDate: FormatDate;
  locale: string;
  /** The PR section's MAXes, read once by the report for both drawings. */
  maxHistory: MaxHistory;
  /** A shared page: the page header (product, title, period) on top, and
   *  every panel drawn as a picture (ProgressPictureContext). */
  branded?: boolean;
  /** Branded only: «2/5» in the page header. */
  page?: ReportPageNumber;
  /** One page's rows of a list section (E1RM, balance, PRs). */
  slice?: RowSlice;
}) {
  const { theme, range } = deps;
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const node = (id: ReportSectionId) => {
    switch (id) {
      case "rhythm":
        return <ReportRhythm deps={deps} formatDate={formatDate} locale={locale} />;
      case "e1rmTrends":
        return <E1RMTrendsPanel {...deps} slice={slice} />;
      case "movementBalance":
        return <MovementBalancePanel {...deps} slice={slice} />;
      case "prTimeline":
        return (
          <ProgressPrTimelineCard
            deps={deps}
            formatDate={formatDate}
            locale={locale}
            history={maxHistory}
            slice={slice}
          />
        );
      // The advanced sections are premium: never in a base report.
      default:
        return null;
    }
  };

  return (
    <ProgressPictureContext.Provider value={branded}>
      {/* #388: the period heads the report, so no panel repeats its window. */}
      <PeriodOnScreenContext.Provider value>
        <View style={styles.body}>
          {/* On screen the sheet already says «Training report»: the period
              heads the report. A page names both, and the product. */}
          {branded ? (
            <ReportPageHeader theme={theme} range={range} page={page} />
          ) : (
            <ReportPeriodText
              theme={theme}
              range={range}
             
              style={styles.periodHead}
            />
          )}
          {order.length === 0 ? (
            <Text style={styles.empty}>
              {t("mobile.progress.report.noSections", {
                defaultValue: "No section chosen. Choose the sections to see the report.",
              })}
            </Text>
          ) : (
            <WidgetColumns
              order={order}
              columnOf={(id) => PROGRESS_WIDGET_COLUMN[id]}
              wide={wide}
              renderItem={(id) => <View key={id}>{node(id)}</View>}
              gap={theme.spacing.s4}
            />
          )}
        </View>
      </PeriodOnScreenContext.Provider>
    </ProgressPictureContext.Provider>
  );
}

function makeStyles(theme: ProgressDeps["theme"]) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    body: { gap: s.s4 },
    // On screen the period heads the report.
    periodHead: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    empty: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
